/**
 * ThemeProvider — loads the signed-in user's workspace branding, UI mode
 * (dark/light/system), and font density preference, and injects them as:
 *   - CSS custom properties on <html> (brand colors)
 *   - `data-theme="dark|light"` attribute on <html>
 *   - `data-density="small|medium|large|xlarge"` attribute on <html>
 *
 * Theme + density are persisted to localStorage immediately for pre-boot
 * paint correctness, then synced to the server on login so they follow
 * the user across devices.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const DEFAULT_BRANDING = {
  primary: "#7C3AED",
  accent: "#A78BFA",
  primary_hover: "#6D28D9",
  secondary: "#38BDF8",
  name: "Default Purple",
  logo_url: null,
};

const LS_THEME   = "rd.theme";    // "dark" | "light" | "system"
const LS_DENSITY = "rd.density";  // "small" | "medium" | "large" | "xlarge"

/** Resolve "system" to the actual OS preference. */
function resolveSystemTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function brandingLogoUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/files/${encodeURIComponent(path)}`;
}

const ThemeContext = createContext({
  branding: DEFAULT_BRANDING,
  presets: {},
  loading: false,
  applyPreview: () => {},
  clearPreview: () => {},
  reloadBranding: async () => {},
  theme: "dark",
  density: "medium",
  setTheme: () => {},
  setDensity: () => {},
  resolvedTheme: "dark",
});

export const useTheme = () => useContext(ThemeContext);

export function ThemeProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [presets, setPresets] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // Hydrate theme + density from localStorage synchronously so first paint
  // is already in the right mode (no flash). Falls back to dark for parity
  // with the existing look.
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(LS_THEME) || "dark"; } catch { return "dark"; }
  });
  const [density, setDensityState] = useState(() => {
    try { return localStorage.getItem(LS_DENSITY) || "medium"; } catch { return "medium"; }
  });
  const resolvedTheme = theme === "system" ? resolveSystemTheme() : theme;

  // Apply theme + density attributes on <html>.
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("data-theme", resolvedTheme);
    html.setAttribute("data-density", density);
  }, [resolvedTheme, density]);

  // When "system" is selected, listen for OS preference changes.
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => document.documentElement.setAttribute("data-theme", mq.matches ? "light" : "dark");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  // Public setters — persist to localStorage immediately + sync to server
  // in the background. The optimistic write means the UI feels instant
  // even on a slow network.
  const setTheme = useCallback((v) => {
    setThemeState(v);
    try { localStorage.setItem(LS_THEME, v); } catch {}
    if (user) api.patch("/profile/ui-prefs", { theme: v }).catch(() => {});
  }, [user]);
  const setDensity = useCallback((v) => {
    setDensityState(v);
    try { localStorage.setItem(LS_DENSITY, v); } catch {}
    if (user) api.patch("/profile/ui-prefs", { density: v }).catch(() => {});
  }, [user]);

  const reloadBranding = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get("/branding/mine");
      setBranding(data?.branding ? { ...DEFAULT_BRANDING, ...data.branding } : DEFAULT_BRANDING);
      setPresets(data?.presets || {});
      // Pull server-side ui_prefs — but only trust them if the user hasn't
      // explicitly changed them in this browser (localStorage wins so the
      // user's current session preference isn't yanked out from under them).
      const serverPrefs = data?.ui_prefs || {};
      if (serverPrefs.theme && !localStorage.getItem(LS_THEME)) setThemeState(serverPrefs.theme);
      if (serverPrefs.density && !localStorage.getItem(LS_DENSITY)) setDensityState(serverPrefs.density);
    } catch { /* non-fatal */ }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) reloadBranding();
  }, [authLoading, user, reloadBranding]);

  // Apply brand color CSS variables.
  const effective = preview || branding;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", effective.primary);
    root.style.setProperty("--brand-accent", effective.accent);
    root.style.setProperty("--brand-primary-hover", effective.primary_hover);
    root.style.setProperty("--brand-secondary", effective.secondary);
  }, [effective.primary, effective.accent, effective.primary_hover, effective.secondary]);

  const value = useMemo(() => ({
    branding,
    effective,
    presets,
    loading,
    isPreviewing: !!preview,
    applyPreview: (p) => setPreview({ ...DEFAULT_BRANDING, ...(p || {}) }),
    clearPreview: () => setPreview(null),
    reloadBranding,
    DEFAULT_BRANDING,
    theme,
    density,
    setTheme,
    setDensity,
    resolvedTheme,
  }), [branding, effective, presets, loading, preview, reloadBranding, theme, density, setTheme, setDensity, resolvedTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
